import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Loader2, User, Lock, Smartphone, Store, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { resolvePostLoginPath } from "@/lib/auth-redirect";

const Login = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialUsername = searchParams.get("username") ?? "";
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath =
    (location.state as { from?: string } | null)?.from ||
    searchParams.get("returnUrl") ||
    searchParams.get("redirectTo");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const user = await login(username.trim(), password);
      if (!user) {
        toast({
          title: "Sign in failed",
          description: "Invalid username or password. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({ title: "Welcome back", description: `Signed in as ${user.username || user.email}.` });
      navigate(resolvePostLoginPath(user.role, redirectPath), { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-5 p-6 sm:p-7">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-medium">
              {t("login.username")}
            </Label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder={t("login.username_placeholder")}
                className="h-11 pl-9"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="password" className="text-sm font-medium">
                {t("login.password")}
              </Label>
              <Link
                to="/auth/forgot-password"
                className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
              >
                {t("login.forgot_password")}
              </Link>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("login.password_placeholder")}
                className="h-11 pl-9 pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={submitting}
            />
            <label
              htmlFor="remember"
              className="cursor-pointer select-none text-sm text-muted-foreground"
            >
              {t("login.remember_me")}
            </label>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="h-11 w-full text-sm font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("login.submitting")}
              </>
            ) : (
              t("login.submit")
            )}
          </Button>
        </form>

        <div className="relative px-6 sm:px-7">
          <div className="absolute inset-x-6 top-1/2 h-px bg-border sm:inset-x-7" />
          <p className="relative mx-auto w-fit bg-card px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            or continue with
          </p>
        </div>

        <div className="p-6 pt-5 sm:p-7">
          <Button
            className="h-11 w-full text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white transition-all rounded-xl shadow-xs border-none"
            asChild
          >
            <Link to={`/auth/otp${location.search}`} state={location.state}>
              <Smartphone className="mr-2 h-4 w-4" />
              {t("login.otp")}
            </Link>
          </Button>
        </div>
      </div>

      <div className="space-y-4 pt-2">
        <div className="relative">
          <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          <p className="relative mx-auto w-fit bg-card px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("login.new_title")}
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex flex-col gap-2.5">
            <Button
              className="h-11 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-all rounded-xl shadow-xs w-full border-none"
              asChild
            >
              <Link to="/auth/register">
                <User className="mr-2 h-4 w-4" />
                {t("login.register_user")}
              </Link>
            </Button>

            <Button
              className="h-11 text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white transition-all rounded-xl shadow-xs w-full border-none"
              asChild
            >
              <Link to="/auth/register-store">
                <Store className="mr-2 h-4 w-4" />
                {t("login.register_store")} &rarr;
              </Link>
            </Button>

            <Button
              className="h-11 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-all rounded-xl shadow-xs w-full border-none"
              asChild
            >
              <Link to="/auth/register-delivery">
                <Truck className="mr-2 h-4 w-4" />
                {t("login.join_delivery")}
              </Link>
            </Button>
          </div>
          
          <p className="text-center text-[11px] text-muted-foreground mt-1">
            {t("login.want_to_sell")}{" "}
            <Link to="/become-a-seller" className="underline hover:text-foreground">
              {t("login.seller_info")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
